import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import {
  fetchSearchResults,
  getCompounds,
  getFilterOptions,
  getFooterLinks,
  getNawyNowDevelopers,
  getNawyNowFeatured
} from '../../../new-lib/apis';
import { GetStaticPaths, GetStaticPropsContext } from 'next';
import { wrapper } from '../../../redux/store';
import {
  setExperimentVariant,
  setIsMobile
} from '../../../redux/actions/appActions';
import { StaticPagesUtility } from '../../../new-lib/utilities/static-pages.utility';
import { ConfigsApis } from '../../../new-lib/apis/configs.apis';
import { AreasApis } from '../../../new-lib/apis/areas.apis';
import { PropertyTypesApis } from '../../../new-lib/apis/property-types.apis';
import { SCHEMA_TYPES } from '../../../features/structured-data/components/SchemaBuilder';

import NawyNowContainer from '../../../features/nawy-now';
import { DevelopersApis } from '../../../new-lib/apis/developers.apis';
import { MapsApis } from '../../../new-lib/apis/maps.apis';
import { setMapConfig } from '../../../redux/slices/mapSlice';
import { getNowDate } from '../../../helpers/search-options/common';

const NawyNow = (props: any) => {
  return <NawyNowContainer {...props} />;
};
export default NawyNow;

export const getStaticProps = wrapper.getStaticProps(
  store =>
    async ({ params, locale }: GetStaticPropsContext) => {
      const filters = {
        min_ready_by: {
          date: getNowDate().split('-'),
          exclude_movenow: false
        },
        nawy_now: true,
        show: 'property'
      };
      try {
        const [
          footerLinks,
          configVariables,
          filterOptions,
          propertyTypes,
          areas,
          compounds,
          developers,
          nawyNowDevelopers,
          nawyNowFeatured,
          mapConfig,
          initialResults
        ] = await Promise.all([
          getFooterLinks(locale, 'nawy-now'),
          ConfigsApis.getConfigs(locale),
          getFilterOptions(locale),
          PropertyTypesApis.listAll(locale),
          AreasApis.listAll(locale),
          getCompounds(),
          DevelopersApis.listAll(locale),
          getNawyNowDevelopers(locale),
          getNawyNowFeatured(locale),
          MapsApis.getMapConfig(),
          fetchSearchResults(filters, locale).then(({ data }) => data)
        ]);

        store.dispatch(setIsMobile(params?.device === 'mobile'));
        store.dispatch(setExperimentVariant(params?.variant as string));
        store.dispatch(setMapConfig(mapConfig));

        const faqs =
          locale == 'en'
            ? [
                {
                  question: 'What is the Nawy Now service?',
                  answer:
                    'Nawy Now is a mortgage service that allows you to purchase a property, move in right away, and pay with customized installment plans for up to 10 years.'
                },
                {
                  question: 'What is the available installment period?',
                  answer:
                    'Nawy Now offers flexible payment plans with installments available for up to 10 years.'
                },
                {
                  question: 'What are the steps to apply for financing?',
                  answer:
                    'To apply for financing, simply fill out the contact form, and our team will guide you through the rest of the process.'
                },
                {
                  question: 'Are units delivered immediately?',
                  answer:
                    'Yes, with Nawy Now, you can get your unit and move in within days of your application being approved.'
                },
                {
                  question: 'Which areas are covered by Nawy Now?',
                  answer:
                    'Nawy Now services cover a wide range of areas, including, but not limited to, New Cairo, North Coast, 6th of October, Ain Sokhna, and El Gouna.'
                },
                {
                  question:
                    'Which compounds are eligible for the Nawy Now service?',
                  answer:
                    'You can purchase a unit through Nawy Now in numerous high-end, ready-to-move compounds. These include Sarai Misr City, Porto Sokhna, Mountain View iCity October, Madinaty, Marassi Sidi Abdel Rahman, Amwaj Al Ahly Sabbour, Palm Hills New Cairo, Stone Residence, Mivida, Hacienda Bay, Uptown Cairo, Layan Sabbour, New Giza, and Eastown, among many others.'
                }
              ]
            : [
                {
                  question: 'ما هي خدمة Nawy Now؟',
                  answer:
                    'خدمة تمويل عقاري تقدمها شركة ناوي لعملائها الراغبين في امتلاك وحداتهم السكنية والانتقال إليها على الفور، مع وجود خطط سداد مرنة تصل إلى 10 سنوات.'
                },
                {
                  question: 'ما هي مدة التقسيط المتاحة في خدمة ناوي ناو؟',
                  answer:
                    'تقدم لك شركة ناوي العديد من أنظمة السداد المرنة والمتنوعة في خدمة Nawy Now والتي تصل إلى 10 سنوات في بعض الأحيان.'
                },
                {
                  question:
                    'ما هي خطوات التقديم على التمويل العقاري في Nawy Now؟',
                  answer:
                    'يمكنك التقديم من خلال صفحة ملء النموذج أسفل الصفحة، واختيار وحدتك المناسبة، وسيتم التواصل معك مباشرة لتأكيد البيانات والحصول على الموافقة الفورية.'
                },
                {
                  question: 'هل يوجد تسليم فوري للوحدات في ناوي ناو؟',
                  answer:
                    'نعم، يمكنك الحصول على وحدتك والانتقال إليها خلال أيام.'
                },
                {
                  question: 'ما هي المناطق المتاحة في Nawy Now؟',
                  answer:
                    'تغطي خدمة ناوي ناو العديد من المناطق المختلفة في مصر مثل: القاهرة الجديدة، الساحل الشمالي، مدينة السادس من أكتوبر، العين السخنة، والجونة وغيرها من المناطق الحيوية الأخرى.'
                },
                {
                  question:
                    'ما هي الكمبوندات المتاح التقسيط بها في خدمة Nawy Now؟',
                  answer:
                    'توفر لك ناوي مجموعة كبيرة من الخيارات المختلفة من أفضل كمبوندات مصر والتي يمكنك اختيار ما يتناسب معك ويلبي احتياجاتك من ضمنها (اي كمبوند استلام فوري)، ويأتي من بينها: سراي مدينة مصر، بورتو السخنة، ماونتن فيو اي سيتي أكتوبر، مدينتي، مراسي سيدي عبد الرحمن، أمواج الأهلي صبور، بالم هيلز القاهرة الجديدة، ستون ريزيدنس، ميفيدا، هاسيندا باي، اب تاون كايرو، ليان صبور، نيو جيزة، ايستاون وغيرها من المشروعات العقارية المميزة.'
                }
              ];

        const getTitle = () => {
          return locale === 'en'
            ? 'Nawy Now: Move Now & Pay Later (Over 7 years) - Nawy'
            : 'ناوي ناو Nawy Now: اسكن فورًا وادفع بالتقسيط حتى 7 سنوات - Nawy';
        };

        const getDescription = () => {
          return locale === 'en'
            ? 'Nawy Now offers mortgage options for ready-to-move properties with up to 7 years installments, instant approval and immediate delivery in New Cairo, New Capital & North Coast.'
            : 'تقدم Nawy Now ناوي ناو حلول تمويل عقاري في مصر لشراء شقق وفلل جاهزة بالتقسيط حتى 7 سنوات. استلام فوري، موافقة تمويل خلال ساعات، وأسعار تنافسية في القاهرة الجديدة، العاصمة الإدارية، والساحل الشمالي.';
        };

        return {
          revalidate: 60 * 60 * 12,
          props: {
            faqs: faqs,
            isNawyNow: true,
            areas,
            compounds: compounds.map((compound: any) => ({
              name: compound.name,
              id: compound.id,
              category: 'compound',
              value: compound.id
            })),
            developers,
            nawyNowDevelopers,
            propertyTypes,
            nawyNowFeatured,
            ...(await serverSideTranslations(locale as string, [
              'nawy-now',
              'common',
              'home',
              'search',
              'entity',
              'contact'
            ])),
            filterOptions: JSON.stringify(filterOptions) || '',
            configVariables,
            layout: {
              title: getTitle(),
              metaDescription: getDescription(),
              footerLinks,
              url: `${process.env.NEXT_PUBLIC_HOST}/nawy-now`
            },
            loadedSearchResultsSSR: initialResults,
            schemas: [
              SCHEMA_TYPES.breadcrumb,
              SCHEMA_TYPES.faqs,
              SCHEMA_TYPES.product
            ]
          }
        };
      } catch (error) {
        return {
          notFound: true
        };
      }
    }
);

export const getStaticPaths = (async () => {
  return { paths: StaticPagesUtility.get(), fallback: false };
}) satisfies GetStaticPaths;
